import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { RestrictedContent } from '@/components/common/RestrictedContent';
import { MainLayout } from '@/components/layout/MainLayout';

// Lazy loading de páginas
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Dashboard
const DashboardGeneralPage = lazy(() => import('@/pages/dashboard/DashboardGeneralPage'));
const DashboardCuadraturaPage = lazy(() => import('@/pages/dashboard/DashboardCuadraturaPage'));
const DashboardSuscriptoresPage = lazy(() => import('@/pages/dashboard/DashboardSuscriptoresPage'));
const DashboardFinancieroPage = lazy(() => import('@/pages/dashboard/DashboardFinancieroPage'));

// Suscriptores (anteriormente Medios de pago)
const VirtualPOSPage = lazy(() => import('@/pages/payment-methods/VirtualPOSPage').then(m => ({ default: m.VirtualPOSPage })));
const VirtualPOSKPIsPage = lazy(() => import('@/pages/payment-methods/VirtualPOSKPIsPage').then(m => ({ default: m.VirtualPOSKPIsPage })));
const PaykuPage = lazy(() => import('@/pages/payment-methods/PaykuPage').then(m => ({ default: m.PaykuPage })));
const PaykuKPIsPage = lazy(() => import('@/pages/payment-methods/PaykuKPIsPage').then(m => ({ default: m.PaykuKPIsPage })));
const MandatosPage = lazy(() => import('@/pages/payment-methods/MandatosPage').then(m => ({ default: m.MandatosPage })));
const MandatosKPIsPage = lazy(() => import('@/pages/payment-methods/MandatosKPIsPage').then(m => ({ default: m.MandatosKPIsPage })));
const MandatosUploadsPage = lazy(() => import('@/pages/payment-methods/MandatosUploadsPage').then(m => ({ default: m.MandatosUploadsPage })));
const DonandoPage = lazy(() => import('@/pages/payment-methods/DonandoPage').then(m => ({ default: m.DonandoPage })));
const CarteraPage = lazy(() => import('@/pages/subscribers/CarteraPage'));
const CarteraCuadraturaPage = lazy(() => import('@/pages/subscribers/CarteraCuadraturaPage'));
const SociosAnalogosPage = lazy(() => import('@/pages/socios/SociosAnalogosPage/SociosAnalogosPage').then(m => ({ default: m.SociosAnalogosPage })));

// Control de Gestión
const ForecastPage = lazy(() => import('@/pages/management/ForecastPage'));
const PresupuestoPage = lazy(() => import('@/pages/management/PresupuestoPage'));
const ProyeccionesPage = lazy(() => import('@/pages/management/ProyeccionesPage'));

// Reportes
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));

// Configuración
const UsuariosPage = lazy(() => import('@/pages/settings/UsuariosPage').then(m => ({ default: m.UsuariosPage })));
const IntegrationsPage = lazy(() => import('@/pages/settings/IntegrationsPage'));
const IntegrationLogsPage = lazy(() => import('@/pages/settings/IntegrationLogsPage'));

// Perfil
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));

// Chatbot
const ChatbotPage = lazy(() => import('@/pages/chatbot/ChatbotPage').then(m => ({ default: m.ChatbotPage })));

// Mantenedores
const UsuariosMantenantPage = lazy(() => import('@/pages/mantenants/UsuariosPage/UsuariosMantenantPage').then(m => ({ default: m.UsuariosMantenantPage })));
const EstadosMantenantPage = lazy(() => import('@/pages/mantenants/EstadosPage/EstadosMantenantPage').then(m => ({ default: m.EstadosMantenantPage })));
const BancosMantenantPage = lazy(() => import('@/pages/mantenants/BancosPage/BancosMantenantPage').then(m => ({ default: m.BancosMantenantPage })));

export default function AppRoutes() {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Dashboard - Redirect to General */}
      <Route path="/dashboard" element={<Navigate to="/dashboard/general" replace />} />

      {/* Dashboard */}
      <Route
        path="/dashboard/general"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardGeneralPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/cuadratura"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardCuadraturaPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/suscriptores"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardSuscriptoresPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/financiero"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardFinancieroPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Suscriptores */}
      <Route
        path="/subscribers/cartera"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CarteraPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/cartera/cuadratura"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CarteraCuadraturaPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/virtual-pos"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VirtualPOSPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/virtual-pos/kpis"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VirtualPOSKPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/payku"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PaykuPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/payku/kpis"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PaykuKPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/mandatos"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MandatosPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/mandatos/uploads"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MandatosUploadsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/mandatos/kpis"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MandatosKPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/donando"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DonandoPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/socios-analogos"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SociosAnalogosPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Control de Gestión - Módulos en desarrollo con acceso restringido */}
      <Route
        path="/management/forecast"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RestrictedContent
                requiredPermissions={["management:beta", "management:forecast"]}
                fallbackTitle="Módulo Forecast en Desarrollo"
                fallbackMessage="El módulo de Forecast está actualmente en fase de desarrollo y pruebas. Solo usuarios autorizados pueden acceder en esta etapa."
              >
                <ForecastPage />
              </RestrictedContent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/management/presupuesto"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RestrictedContent
                requiredPermissions={["management:beta", "management:presupuesto"]}
                fallbackTitle="Módulo Presupuesto en Desarrollo"
                fallbackMessage="El módulo de Presupuesto está actualmente en fase de desarrollo y pruebas. Solo usuarios autorizados pueden acceder en esta etapa."
              >
                <PresupuestoPage />
              </RestrictedContent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/management/proyecciones"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RestrictedContent
                requiredPermissions={["management:beta", "management:proyecciones"]}
                fallbackTitle="Módulo Proyecciones en Desarrollo"
                fallbackMessage="El módulo de Proyecciones está actualmente en fase de desarrollo y pruebas. Solo usuarios autorizados pueden acceder en esta etapa."
              >
                <ProyeccionesPage />
              </RestrictedContent>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Reportes */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReportsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Configuración */}
      <Route
        path="/settings/usuarios"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsuariosPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/integrations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <IntegrationsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/integration-logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <IntegrationLogsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Perfil de usuario */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Chatbot */}
      <Route
        path="/chatbot"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ChatbotPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Mantenedores */}
      <Route
        path="/mantenants/usuarios"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsuariosMantenantPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mantenants/estados"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EstadosMantenantPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mantenants/bancos"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BancosMantenantPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirecciones y 404 */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
